"use client";

import { cva, type VariantProps } from "class-variance-authority";
import type { HTMLAttributes } from "react";

import { cn } from "@/lib/utils/cn";

export const badgeVariants = cva(
  "inline-flex max-w-full items-center rounded-full border px-ds-2 py-0.5 text-micro font-semibold uppercase tracking-[0.08em]",
  {
    variants: {
      tone: {
        neutral: "border-card-border bg-surface-secondary text-muted",
        accent: "border-accent/30 bg-accent-muted text-accent",
        success: "border-status-success-border bg-status-success-surface text-status-success",
        warning: "border-status-warning-border bg-status-warning-surface text-status-warning",
        error: "border-status-error-border bg-status-error-surface text-status-error",
        info: "border-status-info-border bg-status-info-surface text-status-info",
        offline: "border-status-offline-border bg-status-offline-surface text-status-offline",
        maintenance: "border-status-maintenance-border bg-status-maintenance-surface text-status-maintenance",
      },
    },
    defaultVariants: { tone: "neutral" },
  },
);

export type BadgeProps = HTMLAttributes<HTMLSpanElement> & VariantProps<typeof badgeVariants>;
export function Badge({ className, tone, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ tone }), className)} {...props} />;
}

const statusDotVariants = cva("rounded-full ring-2 ring-offset-1 ring-offset-surface-primary", {
  variants: {
    variant: {
      online: "bg-status-success ring-status-success-surface",
      success: "bg-status-success ring-status-success-surface",
      offline: "bg-status-offline ring-status-offline-surface",
      warning: "bg-status-warning ring-status-warning-surface",
      error: "bg-status-error ring-status-error-surface",
      info: "bg-status-info ring-status-info-surface",
      maintenance: "bg-status-maintenance ring-status-maintenance-surface",
      neutral: "bg-border-strong ring-surface-secondary",
      accent: "bg-accent ring-accent-muted",
    },
    size: { sm: "h-2 w-2", md: "h-2.5 w-2.5", lg: "h-3 w-3" },
  },
  defaultVariants: { variant: "neutral", size: "md" },
});

export type StatusIndicatorProps = VariantProps<typeof statusDotVariants> & {
  className?: string;
  pulse?: boolean;
  showRing?: boolean;
  label?: string;
};

const ringTone = {
  online: "text-status-success",
  success: "text-status-success",
  offline: "text-status-offline",
  warning: "text-status-warning",
  error: "text-status-error",
  info: "text-status-info",
  maintenance: "text-status-maintenance",
  neutral: "text-muted",
  accent: "text-accent",
} as const;

export function StatusIndicator({ className, variant = "neutral", size, pulse = false, showRing = false, label }: StatusIndicatorProps) {
  const resolvedVariant = variant ?? "neutral";
  return (
    <span className={cn("relative inline-flex shrink-0 items-center justify-center", className)} title={label} aria-label={label}>
      {showRing ? <span className={cn("absolute h-4 w-4 rounded-full bg-current opacity-30 animate-status-ring", ringTone[resolvedVariant])} /> : null}
      <span className={cn(statusDotVariants({ variant: resolvedVariant, size }), pulse && "animate-status-live", showRing && "relative z-[1]")} />
    </span>
  );
}
